import React, { useState } from 'react';
import { NODE_TYPES } from '../constants.js';
import { DelegationType } from '../types.js';
import { DeleteIcon, WarningIcon, DelegationIcon, CommentIcon, ExecutionIcon } from './icons';

const Handle = ({ 
    onMouseDown, 
    onMouseUp, 
    className, 
    title, 
    colorClass = 'border-dark-border', 
    isHighlighted 
}) => (
    <div 
        title={title}
        className={`absolute w-3 h-3 bg-dark-surface border-2 rounded-full cursor-crosshair z-10 transition-transform ${className} ${colorClass} ${isHighlighted ? 'scale-150 bg-brand-primary' : ''}`}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
    />
);

export const BusinessPlanNode = ({ 
    node, 
    onClick, 
    isSelected, 
    onMouseDown, 
    onDelete, 
    onStartDrawingEdge, 
    onMouseUpOnInput,
    isDrawingEdge,
    drawingEdgeSourceId,
    hoveredInputTargetId,
    onSetHoveredInputTarget,
    isExecuting,
    onOpenDelegationPanel,
    onAddComment,
    comments = [],
    executionLogs = [],
    // Add nodes prop to access all nodes in the workflow
    nodes = []
}) => {
  const nodeInfo = NODE_TYPES[node.type];
  const isEndNode = node.type === 'LOOP_END';
  const hasWarnings = node.warnings && node.warnings.length > 0;
  const hasDelegation = node.data.delegation && node.data.delegation.delegationType !== DelegationType.NONE;
  const [showComments, setShowComments] = useState(false);
  const [showExecutionLogs, setShowExecutionLogs] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showCommentContent, setShowCommentContent] = useState(false); // Add state for COMMENT node content

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete(node.id);
  };
  
  const handleMouseDown = (e) => {
    if ((e.target).closest('[title]')) {
      return;
    }
    onMouseDown(e, node.id);
  };
  
  const handleHandleMouseDown = (e, sourceHandle) => {
    e.stopPropagation();
    onStartDrawingEdge(node.id, sourceHandle);
  };

  const handleInputMouseUp = (e) => {
    e.stopPropagation();
    onMouseUpOnInput(node.id);
  };
  
  const handleDelegationClick = (e) => {
    e.stopPropagation();
    onOpenDelegationPanel(node.id);
  };
  
  const handleAddComment = (e) => {
    e.stopPropagation();
    if (newComment.trim()) {
      onAddComment(node.id, newComment.trim());
      setNewComment('');
    }
  };
  
  const isPotentialTarget = isDrawingEdge && node.id !== drawingEdgeSourceId;

  // Get business plan specific data
  const assignedTo = node.data.assignedTo || 'Unassigned';
  const priority = node.data.priority || 'MEDIUM';
  const estimatedDuration = node.data.estimatedDuration || 'N/A';
  const requiredDocuments = node.data.requiredDocuments || [];
  const todoItems = node.data.todoItems || [];
  const successCriteria = node.data.successCriteria || '';
  const phase = node.data.phase || 'PLANNING';
  const commentText = node.data.text || node.data.commentText || 'Add your comment here...'; // For COMMENT nodes

  // For TODO_LIST nodes, get the actual todo items
  const actualTodoItems = node.type === 'TODO_LIST' ? (node.data.items || []) : todoItems;
  
  // Count completed items for TODO_LIST nodes
  const completedCount = node.type === 'TODO_LIST' 
    ? actualTodoItems.filter(item => item.completed || false).length 
    : 0;
  const totalCount = node.type === 'TODO_LIST' ? actualTodoItems.length : 0;

  // For TODO_VIEW nodes, count all todo items from TODO_LIST nodes
  const todoViewCount = node.type === 'TODO_VIEW' 
    ? nodes
        .filter(n => n.type === 'TODO_LIST')
        .flatMap(n => n.data.items || [])
        .length 
    : 0;

  // Priority color mapping
  const priorityColors = {
    CRITICAL: 'bg-red-500',
    HIGH: 'bg-red-500',
    MEDIUM: 'bg-yellow-500',
    LOW: 'bg-green-500'
  };

  return (
    <div
      className={`absolute w-52 p-3 rounded-lg shadow-xl transition-all duration-200 flex items-center gap-3 bg-dark-surface border ${
        isSelected ? 'border-brand-primary ring-2 ring-brand-primary/50' : 'border-dark-border hover:border-brand-secondary/50'
      } ${isEndNode ? 'h-14' : node.type === 'COMMENT' ? 'h-24' : node.type === 'TODO_VIEW' ? 'h-24' : 'h-20'} ${isExecuting ? 'border-green-500 ring-2 ring-green-500/50 shadow-[0_0_15px_3px_rgba(74,222,128,0.7)]' : ''}`}
      style={{ top: `${node.position.y}px`, left: `${node.position.x}px`, cursor: 'pointer' }}
      onClick={(e) => {
        if (node.type === 'COMMENT') {
          e.stopPropagation();
          setShowCommentContent(!showCommentContent);
        } else {
          onClick(node.id, e.shiftKey);
        }
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => isPotentialTarget && onSetHoveredInputTarget(node.id)}
      onMouseLeave={() => isPotentialTarget && onSetHoveredInputTarget(null)}
    >
      {isSelected && !isExecuting && (
        <button 
          onClick={handleDeleteClick}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white hover:bg-red-700 transition-colors z-20"
          aria-label={`Delete ${node.label} node`}
        >
          <DeleteIcon className="w-4 h-4" />
        </button>
      )}
      
      {hasWarnings && (
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-dark-bg z-10 group">
          <WarningIcon className="w-4 h-4" />
          <div className="absolute bottom-full mb-2 w-max max-w-xs bg-dark-bg text-white text-xs rounded py-1 px-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            <ul className="list-disc list-inside">
              {node.warnings?.map((warning, i) => <li key={i}>{warning}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* Delegation indicator - Always show when delegation is configured */}
      {hasDelegation && (
        <div className="absolute -top-2 -right-8 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-dark-bg z-10 group">
          <DelegationIcon className="w-3 h-3" />
          <div className="absolute bottom-full mb-2 w-max max-w-xs bg-dark-bg text-white text-xs rounded py-1 px-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            {node.data.delegation.delegationType === DelegationType.HUMAN ? 
              `Delegated to: ${node.data.delegation.delegateToHuman.contactName || 'Human'}` : 
              `Delegated to: ${node.data.delegation.delegateToAgent.agentName || 'Agent'}`}
          </div>
        </div>
      )}

      {/* Comment indicator */}
      {comments.length > 0 && (
        <div 
          className="absolute -top-2 left-8 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-dark-bg z-10 group cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setShowComments(!showComments);
          }}
        >
          <CommentIcon className="w-3 h-3" />
          <div className="absolute bottom-full mb-2 w-max max-w-xs bg-dark-bg text-white text-xs rounded py-1 px-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            {comments.length} comment{comments.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Execution log indicator */}
      {executionLogs.length > 0 && (
        <div 
          className="absolute -top-2 left-16 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-dark-bg z-10 group cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setShowExecutionLogs(!showExecutionLogs);
          }}
        >
          <ExecutionIcon className="w-3 h-3" />
          <div className="absolute bottom-full mb-2 w-max max-w-xs bg-dark-bg text-white text-xs rounded py-1 px-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            {executionLogs.length} execution log{executionLogs.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Delegation button - Only show when node is selected */}
      {isSelected && (
        <button
          onClick={handleDelegationClick}
          className="absolute -top-2 -left-8 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white hover:bg-indigo-700 transition-colors z-20"
          aria-label={`Delegate ${node.label} node`}
        >
          <DelegationIcon className="w-3 h-3" />
        </button>
      )}

      <div className="flex-shrink-0 p-2 rounded-md text-brand-primary">
         {nodeInfo?.icon || <div className="w-6 h-6 bg-gray-500 rounded-md"></div>}
      </div>
      <div className="overflow-hidden">
        <p className="font-bold text-sm text-dark-text-primary truncate">{node.label}</p>
        
        {/* Display comment text for COMMENT nodes */}
        {node.type === 'COMMENT' ? (
          <p className="text-xs text-dark-text-secondary mt-1 truncate">
            {commentText}
          </p>
        ) : node.type === 'TODO_VIEW' ? (
          // Display todo count for TODO_VIEW nodes
          <div className="mt-2">
            <div className="text-center">
              <div className="text-2xl font-bold text-brand-primary">{todoViewCount}</div>
              <div className="text-xs text-dark-text-secondary">Total Tasks</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1 mt-1">
            <span className={`text-xs px-1.5 py-0.5 rounded ${priorityColors[priority]} text-white`}>
              {priority}
            </span>
            <span className="text-xs text-dark-text-secondary">{assignedTo}</span>
          </div>
        )}
        
        {/* Show progress for TODO_LIST nodes */}
        {node.type === 'TODO_LIST' && totalCount > 0 && (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-dark-text-secondary mb-1">
              <span>{completedCount}/{totalCount} completed</span>
              <span>{Math.round((completedCount / totalCount) * 100)}%</span>
            </div>
            <div className="w-full bg-dark-bg rounded-full h-1.5">
              <div 
                className="bg-brand-primary h-1.5 rounded-full" 
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      
      {node.type !== 'TRIGGER' && node.type !== 'COMMENT' && node.type !== 'TODO_VIEW' && 
        <Handle 
            title="Input" 
            className="-left-1.5 top-1/2 -translate-y-1/2" 
            onMouseUp={handleInputMouseUp}
            isHighlighted={isPotentialTarget && hoveredInputTargetId === node.id}
        />
      }

      {node.type === 'IF_CONDITION' ? (
        <>
          <Handle title="True path" className="-right-1.5 top-1/4 -translate-y-1/2" onMouseDown={(e) => handleHandleMouseDown(e, 'true')} colorClass="border-green-500 hover:ring-2 hover:ring-green-400" />
          <Handle title="False path" className="-right-1.5 top-3/4 -translate-y-1/2" onMouseDown={(e) => handleHandleMouseDown(e, 'false')} colorClass="border-red-500 hover:ring-2 hover:ring-red-400"/>
        </>
      ) : node.type === 'LOOP_START' ? (
         <>
          <Handle title="Loop Body" className="-right-1.5 top-1/4 -translate-y-1/2" onMouseDown={(e) => handleHandleMouseDown(e, 'loopBody')} colorClass="border-purple-400 hover:ring-2 hover:ring-purple-300"/>
          <Handle title="After Loop" className="-right-1.5 top-3/4 -translate-y-1/2" onMouseDown={(e) => handleHandleMouseDown(e, 'afterLoop')} colorClass="border-gray-500 hover:ring-2 hover:ring-gray-400"/>
        </>
      ) : node.type === 'LOOP_END' || node.type === 'COMMENT' || node.type === 'TODO_VIEW' ? null : (
        <Handle title="Output" className="-right-1.5 top-1/2 -translate-y-1/2" onMouseDown={handleHandleMouseDown}/>
      )}

      {/* Comment content panel for COMMENT nodes */}
      {node.type === 'COMMENT' && showCommentContent && (
        <div 
          className="absolute top-full left-0 mt-2 w-64 bg-dark-surface border border-dark-border rounded-lg shadow-lg z-30"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 border-b border-dark-border">
            <h4 className="font-bold text-dark-text-primary">Comment</h4>
          </div>
          <div className="p-3">
            <p className="text-sm text-dark-text-primary whitespace-pre-wrap">{commentText}</p>
            <p className="text-xs text-dark-text-tertiary mt-2">
              {node.data.author && `Author: ${node.data.author}`}
              {node.data.createdAt && ` • ${new Date(node.data.createdAt).toLocaleString()}`}
            </p>
          </div>
        </div>
      )}

      {/* Comments panel */}
      {showComments && (
        <div 
          className="absolute top-full left-0 mt-2 w-64 bg-dark-surface border border-dark-border rounded-lg shadow-lg z-30"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 border-b border-dark-border">
            <h4 className="font-bold text-dark-text-primary">Comments</h4>
          </div>
          <div className="max-h-40 overflow-y-auto p-3">
            {comments.length > 0 ? (
              <ul className="space-y-2">
                {comments.map((comment, index) => (
                  <li key={index} className="text-sm text-dark-text-secondary border-b border-dark-border pb-2 last:border-b-0">
                    <p>{comment.text}</p>
                    <p className="text-xs text-dark-text-tertiary mt-1">
                      {new Date(comment.timestamp).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-dark-text-tertiary italic">No comments yet</p>
            )}
          </div>
          <div className="p-3 border-t border-dark-border">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition mb-2"
              rows="2"
            />
            <button
              onClick={handleAddComment}
              className="px-3 py-1 bg-brand-primary text-white rounded-md hover:bg-brand-primary/90 transition-colors text-sm"
            >
              Add Comment
            </button>
          </div>
        </div>
      )}

      {/* Execution logs panel */}
      {showExecutionLogs && (
        <div 
          className="absolute top-full left-0 mt-2 w-64 bg-dark-surface border border-dark-border rounded-lg shadow-lg z-30"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 border-b border-dark-border">
            <h4 className="font-bold text-dark-text-primary">Execution Logs</h4>
          </div>
          <div className="max-h-40 overflow-y-auto p-3">
            {executionLogs.length > 0 ? (
              <ul className="space-y-2">
                {executionLogs.map((log, index) => (
                  <li key={index} className="text-sm text-dark-text-secondary border-b border-dark-border pb-2 last:border-b-0">
                    <p className={log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : ''}>
                      {log.message}
                    </p>
                    <p className="text-xs text-dark-text-tertiary mt-1">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-dark-text-tertiary italic">No execution logs yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};